import { Assert } from "../../Assert";
import { IDataSource } from "../../Data/DataSource";
import { PropertyPermissionEvaluator } from "../../Evaluation/PropertyPermissionEvaluator";
import { PartialNullable } from "../../Extensions";
import { ObjectHelper } from "../../Helpers/ObjectHelper";
import { PathHelper } from "../../Helpers/PathHelper";
import { StringHelper } from "../../Helpers/StringHelper";
import { IJNativeEvent } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean, PropertyString } from "../../RadObject";
import { WindowShim } from "../../RadWindow";
import { ClientAwareEntityIdAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework.Data.Entities/ClientAwareEntityIdAttribute";
import { ClientAwareLookupItemAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework.Data.Entities/ClientAwareLookupItemAttribute";
import { AndEvaluator } from "../../Server/DotNet/SeriouslyRAD.Framework.Data.Entities/Evaluation/Evaluators/AndEvaluator";
import { FalseEvaluator } from "../../Server/DotNet/SeriouslyRAD.Framework.Data.Entities/Evaluation/Evaluators/FalseEvaluator";
import { ClientAwareCategoryAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareCategoryAttribute";
import { ClientAwareDecimalAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareDecimalAttribute";
import { ClientAwareDisplayNameAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareDisplayNameAttribute";
import { ClientAwareHelpTextAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareHelpTextAttribute";
import { ClientAwareMandatoryEvaluatorAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareMandatoryEvaluatorAttribute";
import { ClientAwareReadOnlyAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareReadOnlyAttribute";
import { ClientAwareRelevantEvaluatorAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareRelevantEvaluatorAttribute";
import { ClientAwareStringLengthAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareStringLengthAttribute";
import { Evaluator } from "../../Server/DotNet/SeriouslyRAD.Framework/Evaluation/Evaluator";
import { IEvaluator } from "../../Server/DotNet/SeriouslyRAD.Framework/Evaluation/IEvaluator";
import { ITypeInfoMetadata } from "../../Server/DotNet/SeriouslyRAD.Framework/Reflection/ITypeInfoMetadata";
import { IPropertyInfo, PropertyInfo } from "../../Server/DotNet/SeriouslyRAD.Framework/Reflection/PropertyInfo";
import { ITypeInfo, TypeInfo } from "../../Server/DotNet/SeriouslyRAD.Framework/Reflection/TypeInfo";
import { BuiltInTypes } from "../../Server/DotNet/System/BuiltInTypes";
import { Nullable } from "../../Server/DotNet/System/Nullable";
import { ElementRef } from "../ElementRef";
import { JsxFragment } from "../JsxFragment";
import { IDetailsPage } from "../Pages/DetailsPage";
import { IPage } from "../Pages/Page";
import { ComboBoxLookupItem } from "./ComboBoxLookupItem";
import { ComboBoxYesNo } from "./ComboBoxYesNo";
import { IControl } from "./Control";
import { ControlRef } from "./ControlRef";
import { Date } from "./Date";
import { DateTime } from "./DateTime";
import { EntityPicker } from "./EntityPicker";
import { IInputControl, InputControl, InputControlPropertyValues } from "./InputControl";
import { Label } from "./Label";
import { NumericUpDown } from "./NumericUpDown";
import { Text } from "./Text";
import { TextBox, TextBoxTypeEnum } from "./TextBox";
import { Time } from "./Time";
import { TimeSpan } from "./TimeSpan";
import { ValidationPanel } from "./ValidationPanel";
import { NAMESPACE } from "./_Namespace";

import "./PropertyEditor.scss";

const
    radWindow = WindowShim();

export type PropertyEditorNewPropertyValues<T extends ITypeInfoMetadata = ITypeInfoMetadata> =
    InputControlPropertyValues
    & {
        _typeInfoMetadata: T;
        disablePropertyPermissions: boolean;
        editableEvaluator: Evaluator;
        editMode: boolean;
        editorControl: IInputControl;
        helpText: string;
        hideLabel: boolean;
        labelColon: boolean;
        propertyDisplayName: string;
        propertyName: keyof T["properties"];
        relevantEvaluator: IEvaluator;
        showHelpTextAsLabelDuringEdit: boolean;
        showInlineValidationMessages: boolean;
        typeInfo: ITypeInfo,
        validationErrors: any[];
        validationMessages: any[];
        viewControl: IInputControl;
    };

export type PropertyEditorNewProperties<T extends ITypeInfoMetadata = ITypeInfoMetadata> = {
    [name in keyof PropertyEditorNewPropertyValues<T>]: Property<PropertyEditorNewPropertyValues<T>[name]>
};

export interface IPropertyEditorNew<T extends ITypeInfoMetadata = ITypeInfoMetadata> extends IInputControl, PropertyEditorNewProperties<T>
{
}

export interface IPropertyBoundControlFactory
{
    factory: (propertyInfo: PropertyInfo) => Promise<IInputControl>;
    priority?: number;
    dotNetTypes?: BuiltInTypes[];
}

const
    editorControlOverrides: ((propertyInfo: IPropertyInfo) => IInputControl | undefined)[] = [],
    viewControlOverrides: ((propertyInfo: IPropertyInfo) => IInputControl | undefined)[] = [];

export class PropertyEditorNew<T extends ITypeInfoMetadata = ITypeInfoMetadata> extends InputControl implements IPropertyEditorNew<T>
{
    // #region Static

    public static override classNamespace = NAMESPACE + "PropertyEditorNew";
    public static override className = "PropertyEditorNew";

    // #region Global Variables

    // #endregion

    // #region Public Methods

    public static registerEditorControlOverride(override: (propertyInfo: IPropertyInfo) => IInputControl | undefined)
    {
        editorControlOverrides.push(override);
    }
    public static registerViewControlOverride(override: (propertyInfo: IPropertyInfo) => IInputControl | undefined)
    {
        viewControlOverrides.push(override);
    }

    // #endregion

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<PropertyEditorNewPropertyValues<ITypeInfoMetadata>>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                dataSourceControlSelector: ".rad-control-page",
                editMode: false,
                hideLabel: false,
                labelColon: true,
                mandatory: false,
                showInlineValidationMessages: false,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected editElementRef?: ElementRef;
    protected viewElementRef?: ElementRef;
    protected helpTextControlRef?: ControlRef<Text>;
    protected labelControlRef?: ControlRef<Label>;
    protected processControlValueChanged?: boolean;
    protected propertyInfo?: IPropertyInfo;
    protected validationPanel?: ValidationPanel;

    // #endregion

    // #region Properties

    public _typeInfoMetadata!: Property<T>;
    public disablePropertyPermissions!: PropertyBoolean;
    public editableEvaluator!: Property<Evaluator>;
    public editMode!: PropertyBoolean;
    public editorControl!: Property<IInputControl>;
    public helpText!: Property<string>;
    public hideLabel!: PropertyBoolean;
    public labelColon!: PropertyBoolean;
    public propertyDisplayName!: Property<string>;
    public propertyName!: Property<keyof T["properties"]>;
    public relevantEvaluator!: Property<IEvaluator>;
    public showHelpTextAsLabelDuringEdit!: PropertyBoolean;
    public showInlineValidationMessages!: PropertyBoolean;
    public typeInfo!: Property<ITypeInfo>;
    public validationErrors!: Property<any[]>;
    public validationMessages!: Property<string[]>;
    public viewControl!: Property<IInputControl>;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<PropertyEditorNewPropertyValues<T>>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-property-editor field");
        this.processControlValueChanged = true;

        // Trigger Property Changes
        this.editMode.triggerChange();
        this.editorControl.triggerChange();
        this.hideLabel.triggerChange();
        this.propertyDisplayName.triggerChange();
        this.showInlineValidationMessages.triggerChange();
        this.validationErrors.triggerChange();
        this.validationMessages.triggerChange();
    }

    // #endregion

    // #region Public Methods

    public async loadDataAsync(): Promise<void>
    {
        const
            editorControl = this.editorControl.get() as any;

        if (editorControl?.loadDataAsync)
        {
            await editorControl.loadDataAsync();
            this.refreshFromDataSource();
            this._updateLayout();
        }
    }

    // #endregion

    // #region Protected Methods

    protected _controlValueGet()
    {
        return this.editorControl.get()?.value.get();
    }
    protected _controlValueSet(value: any)
    {
        this.processControlValueChanged = false;
        this.editorControl.get()?.value.set(value);
        this.processControlValueChanged = true;
    }
    protected async _createControlsAsync()
    {
        await this._setTypeInfoAsync();
        this.propertyInfo = this._getPropertyInfo();

        const
            viewControl = this._createViewControl();

        viewControl.appendTo(this.viewElementRef?.current);

        this.viewControl.set(viewControl);

        const
            editorControl = this._createEditorControl();

        this.labelControlRef?.current?.associatedControlRef.set(new ControlRef(editorControl));

        // Make sure the property editor value is the same as the editorControl default.
        this.value.set(editorControl.value.get());

        editorControl.appendTo(this.editElementRef?.current);

        this.editorControl.set(editorControl);

        await this.loadDataAsync();

        this._updateLayout();
    }
    protected _createEditorControl(): IInputControl
    {
        let
            editorControl = this.editorControl.get();

        const
            propertyName = this.propertyName.get() as string | undefined,
            entityIdAttribute = this.propertyInfo?.getAttribute(ClientAwareEntityIdAttribute),
            displayNameAttribute = this.propertyInfo?.getAttribute(ClientAwareDisplayNameAttribute),
            helpTextAttribute = this.propertyInfo?.getAttribute(ClientAwareHelpTextAttribute),
            mandatoryEvaluatorAttribute = this.propertyInfo?.getAttribute(ClientAwareMandatoryEvaluatorAttribute),
            readOnlyAttribute = this.propertyInfo?.getAttribute(ClientAwareReadOnlyAttribute);

        if (!editorControl)
        {
            Assert.isNotNullOrUndefined(this.propertyInfo, "this.propertyInfo");

            let
                propertyTypeId = this.propertyInfo.propertyTypeId as BuiltInTypes;

            const
                nullableUnderlyingType = Nullable.getUnderlyingType(propertyTypeId);

            if (nullableUnderlyingType)
                propertyTypeId = nullableUnderlyingType;

            if (!editorControl)
            {
                for (const override of editorControlOverrides)
                {
                    editorControl = override(this.propertyInfo) ?? null;

                    if (editorControl)
                        break;
                }
            }

            if (!editorControl)
            {
                switch (propertyTypeId as BuiltInTypes)
                {
                    case "System.Boolean, System.Private.CoreLib":
                        editorControl = new ComboBoxYesNo();
                        break;
                    case "System.Byte[], System.Private.CoreLib":
                    case "System.Char, System.Private.CoreLib":
                    case "System.SByte[], System.Private.CoreLib":
                    case "System.String, System.Private.CoreLib":
                        {
                            const
                                stringLengthAttribute = this.propertyInfo.getAttribute(ClientAwareStringLengthAttribute);

                            if (stringLengthAttribute)
                                editorControl = new TextBox({ maximumLength: stringLengthAttribute.maximumLength });
                            else
                                editorControl = new TextBox({ type: TextBoxTypeEnum.multiline });

                            break;
                        }
                    case "System.DateOnly, System.Private.CoreLib":
                        editorControl = new Date();
                        break;
                    case "System.DateTime, System.Private.CoreLib":
                        editorControl = new DateTime();
                        break;
                    case "System.Decimal, System.Private.CoreLib":
                    case "System.Double, System.Private.CoreLib":
                    case "System.Int16, System.Private.CoreLib":
                    case "System.Int32, System.Private.CoreLib":
                    case "System.Int64, System.Private.CoreLib":
                    case "System.Single, System.Private.CoreLib":
                    case "System.UInt16, System.Private.CoreLib":
                    case "System.UInt32, System.Private.CoreLib":
                    case "System.UInt64, System.Private.CoreLib":
                        {
                            const
                                decimalAttribute = this.propertyInfo.getAttribute(ClientAwareDecimalAttribute);

                            editorControl = new NumericUpDown({
                                currencySymbol: decimalAttribute?.currencySymbol,
                                precision: decimalAttribute?.precision,
                                scale: decimalAttribute?.scale,
                            });

                            break;
                        }
                    case "System.Guid, System.Private.CoreLib":
                        {
                            if (entityIdAttribute)
                            {
                                editorControl = new EntityPicker({
                                    entityName: entityIdAttribute.entityName,
                                    entityNameFriendly: entityIdAttribute.entityNameFriendly,
                                    entitySetName: entityIdAttribute.entityNamePlural,
                                    entitySetNameFriendly: entityIdAttribute.entityNamePluralFriendly,
                                    entityDescriptionProperty:
                                        entityIdAttribute.entityDescriptionProperty
                                            ? StringHelper.toLowerCamelCase(entityIdAttribute.entityDescriptionProperty)
                                            : null,
                                    entityFilter: entityIdAttribute.entityFilter,
                                });
                                break;
                            }

                            const
                                lookupItemAttribute = this.propertyInfo.getAttribute(ClientAwareLookupItemAttribute);

                            if (lookupItemAttribute)
                            {
                                editorControl = new ComboBoxLookupItem({ lookupType: lookupItemAttribute.typeDescription, });
                                break;
                            }

                            editorControl = new TextBox();
                            break;
                        }
                    case "System.TimeOnly, System.Private.CoreLib":
                        editorControl = new Time();
                        break;
                    case "System.TimeSpan, System.Private.CoreLib":
                        editorControl = new TimeSpan();
                        break;
                    default:
                        throw new Error(`Property type \"${this.propertyInfo.propertyTypeId}\" has not been implemented`);
                }
            }
        }

        if (!this.propertyDisplayName.get())
            this.propertyDisplayName.set(displayNameAttribute?.displayName ?? propertyName ?? null);

        if (!this.helpText.get())
            this.helpText.set(helpTextAttribute?.helpText ?? null);

        this.mandatoryEvaluator.set(mandatoryEvaluatorAttribute?.evaluator?.clone() ?? null);
        this.readOnly.set(!!readOnlyAttribute);

        if (propertyName && !this.dataSourceProperty.get())
            this.dataSourceProperty.set(propertyName);

        return editorControl;
    }
    protected _createViewControl(): IInputControl
    {
        if (!this.propertyInfo)
            return new Text();

        Assert.isNotNullOrUndefined(this.propertyInfo, "this.propertyInfo");

        const
            propertyName = this.propertyName.get();

        Assert.isNotNullOrUndefined(propertyName, "Property name is required to create an editor control.");

        let
            viewControl = this.viewControl.get();

        if (!viewControl)
        {
            for (const override of viewControlOverrides)
            {
                viewControl = override(this.propertyInfo) ?? null;

                if (viewControl)
                    break;
            }
        }

        if (!viewControl)
            viewControl = new Text();

        return viewControl;
    }
    protected _disablePropertyPermissionsChanged(change: PropertyChange<boolean>)
    {
        this._updateLayout();
    }
    protected _editableEvaluatorChanged(change: PropertyChange<IEvaluator>)
    {
        if (change.originalValue)
        {
            change.originalValue.off("valueChanged", { self: this }, this.onEditableEvaluatorValueChanged);
            change.originalValue.context.set(null);
        }

        if (change.currentValue)
        {
            change.currentValue.on("valueChanged", { self: this }, this.onEditableEvaluatorValueChanged);
            change.currentValue.context.set(this);
        }
    }
    protected _editModeChanged(change: PropertyChange<boolean>)
    {
        this._updateLayout();
    }
    protected _editorControlChanged(change: PropertyChange<IInputControl>)
    {
        if (change.originalValue)
            this._editorControlEventsRemove(change.originalValue);

        if (change.currentValue)
        {
            change.currentValue.enabled.set(this.enabled.get());
            this._editorControlEventsAdd(change.currentValue);
        }

        this.labelControlRef?.current?.associatedControlRef.set(new ControlRef(change.currentValue));
    }
    protected _editorControlEventsAdd(editorControl: IControl)
    {
        if (!editorControl)
            return;

        editorControl.on("valueChanged", { self: this }, this.onEditorControlValueChanged);
        editorControl.on("textChanged", { self: this }, this.onEditorControlTextChanged);

        const
            $$focus = editorControl._getFocusElement();

        $$focus?.on("blur", { self: this }, this.onEditorControlBlur);
        $$focus?.on("focus", { self: this }, this.onEditorControlFocus);
    }
    protected _editorControlEventsRemove(editorControl: IControl)
    {
        if (!editorControl)
            return;

        editorControl.off("valueChanged", { self: this }, this.onEditorControlValueChanged);
        editorControl.off("textChanged", { self: this }, this.onEditorControlTextChanged);

        const
            $$focus = editorControl._getFocusElement();

        $$focus?.off("blur", { self: this }, this.onEditorControlBlur);
        $$focus?.off("focus", { self: this }, this.onEditorControlFocus);
    }
    protected _getPropertyInfo(): IPropertyInfo | undefined
    {
        const
            typeInfo = this.typeInfo.get();

        if (!typeInfo)
            return;

        const
            propertyName = this.propertyName.get() as string | undefined;

        Assert.isNotNullOrUndefined(propertyName, "propertyName");

        const
            propertyInfo = typeInfo.properties && typeInfo.properties[propertyName];

        Assert.isNotNullOrUndefined(propertyInfo, `Property info not found for property \"${String(propertyName)}\" in type info \"${typeInfo.underlyingTypeId}\"`);

        return propertyInfo;
    }
    protected _helpTextChanged(change: PropertyChange<string>)
    {
        this._updateLayout();
    }
    protected _isPathControl(primaryEntity?: string, foreignEntityAlias?: string): boolean
    {
        if (!primaryEntity)
            return false;

        if (!foreignEntityAlias)
            return false;

        const
            page = this.page.get();

        if (!ObjectHelper.isAssignableTo<IDetailsPage>(page, "@seriously-rad/seriouslyrad-framework-core/Ui/Pages/DetailsPage.DetailsPage"))
            return false;

        const
            dataSource = this.dataSource.get(),
            dataSourceProperty = this.dataSourceProperty.get();

        if (!dataSource)
            return false;

        const
            url = page.url.get(),
            location = url ? PathHelper.getLocation(url) : null,
            pathParts = location?.applicationRelativeUrl ? StringHelper.trimEnd(location.applicationRelativeUrl, "/").split("/") : [];

        if
            (
            pathParts.length === 4
            && StringHelper.equals(pathParts[0], primaryEntity, { ignoreCase: true })
            && StringHelper.equals(pathParts[2], foreignEntityAlias, { ignoreCase: true })
        )
        {
            if (dataSourceProperty)
            {
                page.dataSourcePropertyFromUrl = dataSourceProperty;
                dataSource.propertyValueByPathSet(dataSourceProperty, pathParts[1]);
            }

            page._loadApplicationEntityPropertiesAsync(dataSource);

            return true;
        }

        return false;
    }
    protected _hideLabelChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.addCssClass("rad-state-label-hidden");
        else
            this.removeCssClass("rad-state-label-hidden");
    }
    protected _labelColonChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _propertyDisplayNameChanged(change: PropertyChange<string>)
    {
        this._updateLayout();
    }
    protected _registerSaveRequests(data: any)
    {
        /* Intentionally empty */
    }
    protected _relevantEvaluatorChanged(change: PropertyChange<IEvaluator>)
    {
        if (change.originalValue)
        {
            change.originalValue.off("valueChanged", { self: this }, this.onRelevantEvaluatorValueChanged);
            change.originalValue.context.set(null);
        }

        if (change.currentValue)
        {
            change.currentValue.on("valueChanged", { self: this }, this.onRelevantEvaluatorValueChanged);
            change.currentValue.context.set(this);

            // TODO: BAM
            //this.editableEvaluator.set(new PropertyPermissionEvaluator({ permission: 4 }));
        }

        this.visibleEvaluator.set(change.currentValue);
    }
    protected async _setTypeInfoAsync(): Promise<void>
    {
        const
            typeInfo = this.typeInfo.get();

        if (typeInfo)
            return;

        const
            typeInfoMetadata = this._typeInfoMetadata.get();

        if (!typeInfoMetadata)
            return;

        let
            typeInfoFromMetadata;

        try
        {
            radWindow.fullPageLoader?.enqueue();
            typeInfoFromMetadata = await TypeInfo.fromTypeInfoMetadataAsync(typeInfoMetadata);
        }
        finally { radWindow.fullPageLoader?.dequeue(); }

        this.typeInfo.set(typeInfoFromMetadata);
    }
    protected _showInlineValidationMessagesChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue && !this.validationPanel)
            this.validationPanel = new ValidationPanel({ shouldScrollToTop: false, titleTextWhenValidationError: StringHelper.empty }).appendTo(this.$$element.get());
    }
    protected _typeInfoChanged(change: PropertyChange<ITypeInfo>)
    {
        /* Intentionally empty */
    }
    protected _updateLayout()
    {
        const
            typeInfo = this.typeInfo.get();

        if (typeInfo)
        {
            Assert.isNotNullOrUndefined(this.propertyInfo, "this.propertyInfo");

            const
                relevantEvaluatorAttribute = this.propertyInfo.getAttribute(ClientAwareRelevantEvaluatorAttribute),
                relevantEvaluator = new AndEvaluator({ context: this });

            if (!this.disablePropertyPermissions.get() && this.propertyInfo.name)
                relevantEvaluator.addEvaluator(new PropertyPermissionEvaluator({ entityName: typeInfo.name, entityPropertyName: this.propertyInfo.name, permission: 2 }));

            if (relevantEvaluatorAttribute?.evaluator)
                relevantEvaluator.addEvaluator(relevantEvaluatorAttribute.evaluator.clone());

            const
                entityIdAttribute = this.propertyInfo.getAttribute(ClientAwareEntityIdAttribute),
                lookupItemAttribute = this.propertyInfo.getAttribute(ClientAwareLookupItemAttribute),
                isPathControl =
                    entityIdAttribute && this._isPathControl(entityIdAttribute.entityNamePlural, entityIdAttribute.foreignEntityAlias)
                    || lookupItemAttribute && this._isPathControl("LookupItems", lookupItemAttribute.foreignEntityAlias);

            if (isPathControl)
                relevantEvaluator.addEvaluator(new FalseEvaluator());

            this.relevantEvaluator.set(relevantEvaluator);
        }

        const
            editorControl = this.editorControl.get(),
            editable = this.editableEvaluator.get()?.evaluate() ?? true,
            editMode = this.editMode.get(),
            readOnly = this.readOnly.get();

        if (editable && editMode && !readOnly)
        {
            const
                showHelpTextAsLabelDuringEdit = this.showHelpTextAsLabelDuringEdit.get(),
                helpText = this.helpText.get();

            let
                helpTextWithoutColon = helpText?.trim();

            if (helpTextWithoutColon && helpTextWithoutColon[helpTextWithoutColon.length - 1] == ":")
                helpTextWithoutColon = helpTextWithoutColon.substring(0, helpTextWithoutColon.length - 1);

            this.labelControlRef?.current?.associatedControlRef.set(new ControlRef(editorControl));
            this.addCssClass("rad-state-edit");
            this.labelControlRef?.current?.text.set(showHelpTextAsLabelDuringEdit && helpTextWithoutColon ? helpTextWithoutColon : this.propertyDisplayName.get());

            if (this.helpTextControlRef?.current)
            {
                this.helpTextControlRef.current.text.set(helpText);
                this.helpTextControlRef.current.visible.set(!!helpText && !showHelpTextAsLabelDuringEdit);
            }
        }
        else
        {
            this.labelControlRef?.current?.associatedControlRef.set(null);
            this.removeCssClass("rad-state-edit");
            this.labelControlRef?.current?.text.set(this.propertyDisplayName.get());

            if (this.helpTextControlRef?.current)
                this.helpTextControlRef.current.visible.set(false);
        }
    }
    protected _updateValidationErrorsFromDataSource()
    {
        const
            dataSource = this.dataSource.get(),
            dataSourceProperty = this.dataSourceProperty.get(),
            dataSourceValidationErrors = dataSource ? dataSource.validationErrors.get() : null;

        this.validationErrors.set(
            dataSourceValidationErrors && dataSourceProperty
                ? dataSourceValidationErrors.filter((item: any) => item.propertyName && StringHelper.toLowerCamelCasePath(item.propertyName) === dataSourceProperty)
                : null
        );
    }
    protected _updateViewControl()
    {
        const
            viewControl = this.viewControl.get();

        if (!viewControl)
            return;

        viewControl.value.set(this.editorControl.get()?.text.get());
    }
    protected _validate()
    {
        const
            validationErrors: { errorMessage: string }[] = [],
            value = this.value.get(),
            valueType = ObjectHelper.typeOf(value),
            hasValue =
                valueType === "string"
                    ? !StringHelper.isNullOrWhitespace(value)
                    : !ObjectHelper.isNullOrUndefined(value);

        if (this.isMandatoryGet() && !hasValue)
        {
            validationErrors.push({
                errorMessage: StringHelper.format("{0} is required.", this.propertyDisplayName.get())
            });
        }

        if (validationErrors.length)
            this.addCssClass("rad-state-error");
        else
            this.removeCssClass("rad-state-error");

        return validationErrors;
    }
    protected _validationErrorsChanged(change: PropertyChange<any[]>)
    {
        this.errorState.set(!!(change.currentValue?.length));
    }
    protected _validationMessagesChanged(change: PropertyChange<string[]>)
    {
        if (this.validationPanel)
            this.validationPanel.setErrorMessages(change.currentValue);
    }
    protected _viewControlChanged(change: PropertyChange<IInputControl>)
    {
        /* Intentionally empty */
    }

    protected onEditableEvaluatorValueChanged(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self.editMode.triggerChange()
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: PropertyEditorNewPropertyValues<T>)
    {
        super.__initializeProperties(propertyValues);

        this._typeInfoMetadata = new Property<T>(this, "System.Object, System.Private.CoreLib", propertyValues._typeInfoMetadata);
        this.disablePropertyPermissions = new PropertyBoolean(this, propertyValues.disablePropertyPermissions, this._disablePropertyPermissionsChanged);
        this.editableEvaluator = new Property<Evaluator>(this, "System.Object, System.Private.CoreLib", propertyValues.editableEvaluator, this._editableEvaluatorChanged);
        this.editMode = new PropertyBoolean(this, propertyValues.editMode, this._editModeChanged);
        this.editorControl = new Property<IInputControl>(this, "System.Object, System.Private.CoreLib", propertyValues.editorControl, this._editorControlChanged);
        this.helpText = new PropertyString({
            context: this,
            initialValue: propertyValues.helpText,
            changeHandler: this._helpTextChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Appearance" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Help Text" }),
            ],
        });
        this.hideLabel = new PropertyBoolean(this, propertyValues.hideLabel, this._hideLabelChanged);
        this.labelColon = new PropertyBoolean(this, propertyValues.labelColon, this._labelColonChanged);
        this.propertyDisplayName = new PropertyString({
            context: this,
            initialValue: propertyValues.propertyDisplayName,
            changeHandler: this._propertyDisplayNameChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Data" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Property Display Name" }),
            ],
        });
        this.propertyName = new Property<keyof T["properties"]>(this, "System.String, System.Private.CoreLib", propertyValues.propertyName);
        this.relevantEvaluator = new Property<IEvaluator>(this, "System.Object, System.Private.CoreLib", propertyValues.relevantEvaluator, this._relevantEvaluatorChanged);
        this.showHelpTextAsLabelDuringEdit = new PropertyBoolean(this, propertyValues.showHelpTextAsLabelDuringEdit);
        this.showInlineValidationMessages = new PropertyBoolean(this, propertyValues.showInlineValidationMessages, this._showInlineValidationMessagesChanged);
        this.typeInfo = new Property<ITypeInfo>(this, "System.Object, System.Private.CoreLib", propertyValues.typeInfo, this._typeInfoChanged);
        this.validationErrors = new Property<any[]>(this, "System.Object[], System.Private.CoreLib", propertyValues.validationErrors, this._validationErrorsChanged);
        this.validationMessages = new Property<string[]>(this, "System.String[], System.Private.CoreLib", propertyValues.validationMessages, this._validationMessagesChanged);
        this.viewControl = new Property<IInputControl>(this, "System.Object, System.Private.CoreLib", propertyValues.viewControl, this._viewControlChanged);

        this.value.overrideSet((value, next) =>
        {
            this._controlValueSet(value);
            value = this._controlValueGet();
            next(value);
            this.updateDataSource();
        });

        this.editElementRef = new ElementRef();
        this.helpTextControlRef = new ControlRef();
        this.labelControlRef = new ControlRef();
        this.viewElementRef = new ElementRef();
    }

    protected override _createElement()
    {
        const
            $$element = super._createElement();

        this._createControlsAsync();

        return $$element;
    }
    protected _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[]): JsxFragment
    {
        return (
            <fieldset>
                <legend>
                    <Label ref={this.labelControlRef} colon={this.labelColon.get()}></Label>
                    <Text ref={this.helpTextControlRef} cssClass="rad-control-help-text" />
                </legend>
                <div ref={this.viewElementRef} class="rad-field-view"></div>
                <div ref={this.editElementRef} class="rad-field-edit"></div>
                <div class="rad-clear-float"></div>
                {children}
            </fieldset>
        );
    }
    protected override _dataSourceChanged(change: PropertyChange<IDataSource>)
    {
        super._dataSourceChanged(change);
        this._updateLayout();
    }
    protected override _dispose()
    {
        this.labelControlRef?.current?.remove();
        this.editorControl.get()?.remove();

        super._dispose();
    }
    protected override _enabledChanged(change: PropertyChange<boolean>)
    {
        const
            $$element = this.$$element.get();

        if (!$$element)
            return;

        super._enabledChanged(change);

        $$element.removeClass("disabled");
        $$element.removeAttr("disabled");

        this.editorControl.get()?.enabled.set(change.currentValue);
    }
    protected override _errorStateChanged(change: PropertyChange<boolean>)
    {
        const
            validationMessages = this.validationMessages.get(),
            hasValidationMessages = Array.isArray(validationMessages) && validationMessages.length,
            showInlineValidationMessages = this.showInlineValidationMessages.get();

        super._errorStateChanged(change);

        const
            editorControl = this.editorControl.get();

        editorControl?.errorState.set(change.currentValue);

        if (change.currentValue && showInlineValidationMessages && hasValidationMessages && this.validationPanel)
        {
            this.validationPanel.setErrorMessages(validationMessages);
            this.validationPanel.show();
        }
        else if (this.validationPanel)
            this.validationPanel.hide();
    }
    protected override _mandatoryChanged(change: PropertyChange<boolean>)
    {
        super._mandatoryChanged(change);
        this.editorControl.get()?.mandatory.set(this.mandatory.get());
    }
    protected override _mandatoryEvaluatorChanged(change: PropertyChange<Evaluator>)
    {
        super._mandatoryEvaluatorChanged(change);
        this.editorControl.get()?.mandatoryEvaluator.set(this.mandatoryEvaluator.get());
    }
    protected override _pageChanged(change: PropertyChange<IPage>)
    {
        super._pageChanged(change);

        if (change.originalValue)
        {
            change.originalValue.off("creatingSaveBatch", { self: this }, this.onPageCreatingSaveBatch);
            change.originalValue.off("editModeChanged", { self: this }, this.onPageEditModeChanged);
        }

        if (change.currentValue)
        {
            change.currentValue.on("creatingSaveBatch", { self: this }, this.onPageCreatingSaveBatch);
            change.currentValue.on("editModeChanged", { self: this }, this.onPageEditModeChanged);

            if (ObjectHelper.isAssignableTo<IDetailsPage>(change.currentValue, "@seriously-rad/seriouslyrad-framework-core/Ui/Pages/DetailsPage.DetailsPage"))
                this.editMode.set(change.currentValue.editMode.get());
        }
    }
    protected override _readOnlyChanged(change: PropertyChange<boolean>)
    {
        super._readOnlyChanged(change);

        const
            editorControl = this.editorControl.get();

        if (editorControl)
            editorControl.readOnly.set(change.currentValue);

        this._updateLayout();
    }
    protected override _valueChanged(change: PropertyChange<any>)
    {
        super._valueChanged(change);
        this._updateViewControl();

        if (change.currentValue)
            this.removeCssClass("rad-state-empty");
        else
            this.addCssClass("rad-state-empty");
    }

    public override remove()
    {
        this.removeControls([
            this.labelControlRef?.current,
            this.editorControl.get(),
        ]);

        super.remove();
    }

    protected override onDataSourceValidationErrorsChanged(evt: IJNativeEvent, change: any)
    {
        const
            self = evt.data.self as this;

        self._updateValidationErrorsFromDataSource();
    }

    // #endregion

    // #region Event Handlers

    protected onEditorControlBlur(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self.removeCssClass("rad-state-focussed");
        self.triggerHandler("editorControlBlur");
    }
    protected onEditorControlFocus(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self.addCssClass("rad-state-focussed");
        self.triggerHandler("editorControlFocus");
    }
    protected onEditorControlTextChanged(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self._updateViewControl();
    }
    protected onEditorControlValueChanged(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        if (!self.processControlValueChanged)
            return;

        self.value.set(self._controlValueGet());
        self._updateViewControl();
        self.errorState.set(false);
    }
    protected onPageCreatingSaveBatch(evt: IJNativeEvent, eventData: any)
    {
        const
            self = evt.data.self as this;

        self._registerSaveRequests(eventData);
    }
    protected onPageEditModeChanged(evt: IJNativeEvent, change: PropertyChange<boolean>)
    {
        const
            self = evt.data.self as this;

        self.editMode.set(change.currentValue);
    }
    protected onRelevantEvaluatorValueChanged(evt: IJNativeEvent, change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #endregion
}